import {
  createMultiStyleConfigHelpers,
  StyleThemingProps,
} from '@chakra-ui/react'
import { colorPalette } from '~/themes/colors/colorPalette'
import { themeColorSectionKeys } from '~/themes/colors/colors'
import { textStyleKeys } from '~/themes/textStyles'
import { useCustomMultiStyleConfig } from '~/utils/chakraUtils'
import { getName } from '~/utils/objectUtils'

const { HeaderBackgroundColor, HeaderBorderColor } =
  themeColorSectionKeys.Settings.Profile

const ProfileSettingsStyleParts = [
  'wrapper',
  'profileCard',
  'profileCardWrapper',
  'headerPbx',
  'headerActions',
  'headerExtension',
  'headerPbxText',
  'headerExtensionText',
  'helpSection',
  'helpTitleWrapper',
  'helpTitleText',
  'modalBodyText',
  'skeletonContainer',
  'skeleton',
  'nameWrapper',
  'nameText',
] as const

const helpers = createMultiStyleConfigHelpers(ProfileSettingsStyleParts)

export const ProfileSettingsStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '3.75rem',
      maxWidth: '500px',
    },
    profileCard: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '2rem',
      padding: '2rem',
      // The pbx text adds some top space, so we need to reduce the top padding to make it look right
      borderRadius: '0.5rem',
      borderWidth: '2px',
      borderColor: HeaderBorderColor,
      backgroundColor: HeaderBackgroundColor,
    },
    headerPbx: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
      flex: 1,
      paddingBottom: '2rem',
      borderBottom: `2px solid ${colorPalette.gray[400]}`,
    },
    headerActions: {
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 1,
      alignItems: 'flex-start',
      gap: '0.5rem',

      justifyContent: 'center',
    },
    headerExtension: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: '0.5rem',
    },
    headerPbxText: {
      fontSize: '1rem',
      fontWeight: '700',
    },
    headerExtensionText: {
      textStyle: textStyleKeys.Body1,
    },
    helpSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      alignItems: 'flex-start',
      paddingBottom: '2rem',
      borderBottom: `2px solid ${colorPalette.gray[400]}`,
    },
    helpTitleWrapper: {
      paddingBottom: '0.375rem',
    },
    helpTitleText: {
      textStyle: textStyleKeys.Body1_Bold,
    },
    modalBodyText: {
      textStyle: textStyleKeys.Body1,
      display: 'inline',
    },
    skeletonContainer: {
      position: 'absolute',
      backgroundColor: HeaderBackgroundColor,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: '0.5rem',
      borderWidth: '2px',
      overflow: 'hidden',
      borderColor: HeaderBorderColor,
    },
    skeleton: {
      borderRadius: 0,
      height: 'full',
      width: 'full',
    },
    nameWrapper: {
      paddingBottom: '2rem',
      flexDirection: 'column',
      alignItems: 'center',
      borderBottom: `2px solid ${colorPalette.gray[400]}`,
      gap: '.25rem',
    },
    nameText: { fontSize: '1.5rem', fontWeight: '700' },
  },
})

export const useProfileSettingsStyle = (props?: StyleThemingProps) => {
  return useCustomMultiStyleConfig<typeof ProfileSettingsStyleParts>(
    getName({ ProfileSettingsStyle }),
    props,
  )
}
